import { Summary } from '@/interfaces';
import { formatCurrency } from '@/utils';
import { usePathname } from 'next/navigation';

const initialStateSummary = [
	{
		type: 'recovered',
		amount: '$0',
	},
	{
		type: 'resolved-cases',
		amount: '0',
	},
	{
		type: 'active-cases',
		amount: '0',
	},
	{
		type: 'on-hold-cases',
		amount: '0',
	},
	{
		type: 'action-required-client',
		amount: '0',
	},
	{
		type: 'action-required-lawyer',
		amount: '0',
	},
	{
		type: 'closed-cases',
		amount: '0',
	},
];

export const useSummaryDashboard = () => {
	const pathname = usePathname();

	const getSummaryOptions = () => {
		if (pathname.includes('financial-recovery')) {
			const validTypes = ['recovered', 'resolved-cases'];
			return initialStateSummary.filter((summary) =>
				validTypes.includes(summary.type)
			);
		}

		if (pathname.includes('clientapp')) {
			const clientOrder = [
				'active-cases',
				'on-hold-cases',
				'resolved-cases',
				'recovered',
			];
			return initialStateSummary
				.filter((summary) => clientOrder.includes(summary.type))
				.sort(
					(a, b) => clientOrder.indexOf(a.type) - clientOrder.indexOf(b.type)
				);
		}

		if (pathname.includes('lawyerapp')) {
			const order = [
				'active-cases',
				'on-hold-cases',
				'action-required-client',
				'action-required-lawyer',
				'recovered',
				'resolved-cases',
				'closed-cases',
			];
			return initialStateSummary.sort((a, b) => {
				const aIndex = order.indexOf(a.type);
				const bIndex = order.indexOf(b.type);
				if (aIndex === -1 && bIndex === -1) {
					return 0;
				} else if (aIndex === -1) {
					return 1;
				} else if (bIndex === -1) {
					return -1;
				} else {
					return aIndex - bIndex;
				}
			});
		}

		return initialStateSummary;
	};

	const getAmountByOption = (
		option: { type: string; amount: string },
		data: Summary
	) => {
		if (option.type === 'recovered') {
			return {
				...option,
				amount: `$${formatCurrency(String(data.totalEarned))}`,
			};
		}

		if (option.type === 'active-cases') {
			return {
				...option,
				amount: `${data.activeCases}`,
			};
		}

		if (option.type === 'resolved-cases') {
			return {
				...option,
				amount: `${data.archivedCases}`,
			};
		}

		if (option.type === 'on-hold-cases') {
			return {
				...option,
				amount: `${data.onholdCases}`,
			};
		}

		return {
			...option,
		};
	};

	const getAmountClientByOption = (
		option: { type: string; amount: string },
		data: Summary
	) => {
		if (option.type === 'active-cases') {
			return {
				...option,
				amount: `${data.activeCases}`,
			};
		}

		if (option.type === 'on-hold-cases') {
			return {
				...option,
				amount: `${data.onholdCases}`,
			};
		}

		if (option.type === 'action-required-client') {
			return {
				...option,
				amount: `${data.clientActionsRequired}`,
			};
		}

		if (option.type === 'action-required-lawyer') {
			return {
				...option,
				amount: `${data.actionsRequriedLawyer}`,
			};
		}

		if (option.type === 'recovered') {
			return {
				...option,
				amount: `$${formatCurrency(String(data.recovered))}`,
			};
		}

		if (option.type === 'resolved-cases') {
			return {
				...option,
				amount: `${data.resolvedCases}`,
			};
		}

		if (option.type === 'closed-cases') {
			return {
				...option,
				amount: `${data.closedCases}`,
			};
		}

		return {
			...option,
		};
	};

	return {
		getSummaryOptions,
		getAmountByOption,
		getAmountClientByOption,
	};
};
